/**
 * File: taxRecords.ts
 * Path: packages/config/src/accounting/taxRecords.ts
 * Description: The things the books need to know so the tax return fills
 * itself: an equipment list (tax's wear-and-tear allowance, s11(e)) and tax
 * tags on accounts and entries (not deductible, s18A donation, penalty).
 * Kept beside the books at accounting/{profileId}/taxRecords/{equipment|tags},
 * written only by the `accounting` callable.
 *
 * Wear and tear: an item under R7,000 (SMALL_ITEM_LIMIT_CENTS) is claimed in full in the year it is
 * bought; anything dearer over three years (the SARS rate for computers and
 * office equipment). SARS may apportion the first year by months in use, so
 * the screen says to check that on a part-year purchase.
 */

import type { Journal, LedgerAccount } from "./accountingTypes";
import { isBooksDate } from "./ledger";
import { SMALL_ITEM_LIMIT_CENTS } from "./zohoImport";

export const TAX_RECORDS_COLLECTION = "taxRecords";
export const EQUIPMENT_DOC = "equipment";
export const TAGS_DOC = "tags";

/** Everything dearer is claimed over this many years, in equal parts. */
export const WEAR_AND_TEAR_YEARS = 3;

export type TaxTag = "notDeductible" | "s18A" | "penalty";

export const TAX_TAGS: readonly { id: TaxTag; label: string; hint: string }[] = [
  { id: "notDeductible", label: "Not deductible (entertainment, private)", hint: "Spending tax doesn't let a company claim, like entertainment or something private. It is added back to the profit." },
  { id: "s18A", label: "s18A donation", hint: "A gift to a charity that gave you an s18A receipt. SARS lets you claim up to 10% of your taxable income." },
  { id: "penalty", label: "Penalty or fine", hint: "A fine or penalty (SARS interest and penalties, traffic fines). Tax never allows these." },
];

export const isTaxTag = (v: unknown): v is TaxTag => v === "notDeductible" || v === "s18A" || v === "penalty";

export interface EquipmentItem {
  id: string;
  name: string;
  boughtOn: string;
  costCents: number;
  /** The day it was sold or scrapped, if it was. */
  soldOn: string | null;
  /** What it was sold for (nil when scrapped). */
  soldForCents: number;
}

export interface TaxTags {
  /** Every expense line in the account counts under this tag. */
  accounts: Record<string, TaxTag>;
  /** One entry's expense lines count under this tag (beats the account's tag). */
  entries: Record<string, TaxTag>;
}

export interface TaxRecords {
  equipment: EquipmentItem[];
  tags: TaxTags;
}

export const emptyTaxRecords = (): TaxRecords => ({ equipment: [], tags: { accounts: {}, entries: {} } });

type Raw = Record<string, unknown>;
const day = (v: unknown): string | null => (typeof v === "string" && isBooksDate(v) ? v : null);
const money = (v: unknown): number => (typeof v === "number" && Number.isSafeInteger(v) && v >= 0 ? v : 0);

export const toEquipment = (data: Raw | undefined | null): EquipmentItem[] => {
  const list = Array.isArray(data?.items) ? (data?.items as unknown[]) : [];
  return list.flatMap((entry): EquipmentItem[] => {
    const r = (entry ?? {}) as Raw;
    const boughtOn = day(r.boughtOn);
    if (typeof r.id !== "string" || typeof r.name !== "string" || !boughtOn) return [];
    return [{ id: r.id, name: r.name, boughtOn, costCents: money(r.costCents), soldOn: day(r.soldOn), soldForCents: money(r.soldForCents) }];
  });
};

const tagMap = (v: unknown): Record<string, TaxTag> => {
  const out: Record<string, TaxTag> = {};
  if (v && typeof v === "object") for (const [key, tag] of Object.entries(v as Raw)) if (isTaxTag(tag)) out[key] = tag;
  return out;
};

export const toTaxTags = (data: Raw | undefined | null): TaxTags => ({ accounts: tagMap(data?.accounts), entries: tagMap(data?.entries) });

/* ---------------------------------------------------------------------------
 * Wear and tear
 * ------------------------------------------------------------------------ */

const yearOf = (day: string) => Number(day.slice(0, 4));

/** The financial year an item was bought in, as its last day's calendar year. */
const buyYearIndex = (item: EquipmentItem, yearEndMonth: number): number => {
  const y = yearOf(item.boughtOn);
  return Number(item.boughtOn.slice(5, 7)) > yearEndMonth ? y + 1 : y;
};

const yearIndexOf = (yearEnd: string): number => yearOf(yearEnd);

/** The scheduled allowance for the `k`th financial year of an item's life (0 = the year it was bought). */
const scheduled = (item: EquipmentItem, k: number): number => {
  if (k < 0) return 0;
  if (item.costCents < SMALL_ITEM_LIMIT_CENTS) return k === 0 ? item.costCents : 0;
  if (k >= WEAR_AND_TEAR_YEARS) return 0;
  const part = Math.round(item.costCents / WEAR_AND_TEAR_YEARS);
  return k < WEAR_AND_TEAR_YEARS - 1 ? part : item.costCents - part * (WEAR_AND_TEAR_YEARS - 1);
};

export interface EquipmentYearRow {
  item: EquipmentItem;
  /** The allowance for the year, including a scrapping allowance in the year it was sold at a loss. */
  allowanceCents: number;
  /** Tax value left after the year's allowance. */
  taxValueCents: number;
  /** Profit on a sale above the tax value, taxed back in the year of the sale. */
  recoupmentCents: number;
}

export interface EquipmentYear {
  rows: EquipmentYearRow[];
  wearAndTearCents: number;
  recoupmentCents: number;
}

/** What the equipment list gives for one financial year (the year ending `yearEnd`). */
export const equipmentForYear = (items: readonly EquipmentItem[], yearEnd: string, yearEndMonth: number): EquipmentYear => {
  const index = yearIndexOf(yearEnd);
  const rows: EquipmentYearRow[] = [];
  for (const item of items) {
    const k = index - buyYearIndex(item, yearEndMonth);
    if (k < 0) continue;
    const soldIndex = item.soldOn ? (Number(item.soldOn.slice(5, 7)) > yearEndMonth ? yearOf(item.soldOn) + 1 : yearOf(item.soldOn)) - buyYearIndex(item, yearEndMonth) : null;
    if (soldIndex !== null && k > soldIndex) continue;
    let claimedBefore = 0;
    for (let i = 0; i < k; i += 1) claimedBefore += scheduled(item, i);
    const taxBefore = item.costCents - claimedBefore;
    if (soldIndex !== null && k === soldIndex) {
      const proceeds = Math.min(item.soldForCents, item.costCents);
      rows.push({
        item,
        allowanceCents: Math.max(0, taxBefore - proceeds),
        taxValueCents: 0,
        recoupmentCents: Math.max(0, proceeds - taxBefore),
      });
      continue;
    }
    const allowanceCents = scheduled(item, k);
    rows.push({ item, allowanceCents, taxValueCents: Math.max(0, taxBefore - allowanceCents), recoupmentCents: 0 });
  }
  return {
    rows,
    wearAndTearCents: rows.reduce((s, r) => s + r.allowanceCents, 0),
    recoupmentCents: rows.reduce((s, r) => s + r.recoupmentCents, 0),
  };
};

/* ---------------------------------------------------------------------------
 * Tax tags
 * ------------------------------------------------------------------------ */

export interface TaggedSpend {
  /** Per tag, the expense spent in the period. */
  totals: Record<TaxTag, number>;
  /** Per account, per tag, so a line can be moved to the line the tag belongs on. */
  byAccount: Map<string, Partial<Record<TaxTag, number>>>;
}

/**
 * Adds up the expense lines the tags cover between two days. An entry's own
 * tag beats its account's, and a reversal follows the tag of the entry it
 * cancels, so a tagged amount that was reversed nets to nil.
 */
export const taggedSpend = (
  journals: readonly Journal[],
  accounts: readonly LedgerAccount[],
  tags: TaxTags,
  from: string,
  to: string,
): TaggedSpend => {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const entryTag = (journal: Journal): TaxTag | undefined => tags.entries[journal.reversalOf ?? journal.id];
  const totals: Record<TaxTag, number> = { notDeductible: 0, s18A: 0, penalty: 0 };
  const byAccount = new Map<string, Partial<Record<TaxTag, number>>>();
  for (const journal of journals) {
    if (journal.date < from || journal.date > to) continue;
    const own = entryTag(journal);
    for (const line of journal.lines) {
      const account = byId.get(line.accountId);
      if (!account || account.type !== "expense") continue;
      const tag = own ?? tags.accounts[account.id];
      if (!tag) continue;
      const amount = line.debitCents - line.creditCents;
      totals[tag] += amount;
      const row = byAccount.get(account.id) ?? {};
      row[tag] = (row[tag] ?? 0) + amount;
      byAccount.set(account.id, row);
    }
  }
  return { totals, byAccount };
};
